import React, { useState } from 'react';
import {
  McCard,
  McInput,
  McButton,
  McLoadingIndicator,
  McBadge,
  McAccordion,
  McAccordionItem,
  McIcon
} from '@maersk-global/mds-react-wrapper';
import { useQuery } from '@tanstack/react-query';
import { regionService } from '../services/regionService';
import { RegionConfig, RegionStats } from '../types';
import FullScreenLoader from '../components/Common/FullScreenLoader';

export default function RegionsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);

  // Fetch region statistics
  const {
    data: stats,
    isLoading: isLoadingStats,
    error: statsError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['regionStats'],
    queryFn: regionService.getRegionStats,
  });

  // Fetch all regions
  const {
    data: regions,
    isLoading: isLoadingRegions,
    error: regionsError,
    refetch: refetchRegions,
  } = useQuery({
    queryKey: ['regions'],
    queryFn: regionService.getAllRegions,
  });

  const handleToggleRegion = (regionName: string) => {
    setExpandedRegion(expandedRegion === regionName ? null : regionName);
  };

  const handleRefresh = () => {
    refetchStats();
    refetchRegions();
  };

  // Filter terminals by search term
  const filterTerminals = (terminals: string[] | null | undefined) => {
    if (!terminals) return [];
    if (!searchTerm) return terminals;
    return terminals.filter((t) => t.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  // Region color mapping
  const getRegionColor = (regionName: string): string => {
    const colors: Record<string, string> = {
      EMEA: '#1976d2', // Blue
      NAM: '#388e3c', // Green
      APAC: '#f57c00', // Orange
      LATM: '#7b1fa2', // Purple
    };
    return colors[regionName] || '#616161';
  };

  const isLoading = isLoadingStats || isLoadingRegions;
  const error = statsError || regionsError;

  if (isLoading) return <FullScreenLoader />;

  return (
    <>
      {/* Header */}
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 className="mds-headline--medium-bold" style={{ marginBottom: '8px' }}>
          Region Management
        </h1>
        <p className="mds-text--medium-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', margin: 0 }}>
          Manage terminal regions and their specific configurations. View active regions and connection details.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
        <McButton
          variant="plain"
          appearance="neutral"
          onClick={handleRefresh}
          disabled={isLoading}
        >
          <McIcon icon="rotate" slot="prefix" />
          Refresh Data
        </McButton>
      </div>

      {/* Stats Dashboard */}
      {error ? (
        <div style={{ padding: '16px', backgroundColor: 'var(--mds-color-feedback-error-background)', border: '1px solid var(--mds-color-feedback-error-border)', borderRadius: '4px', marginBottom: '24px', color: 'var(--mds-color-feedback-error-text)' }}>
          Error loading region data: {(error as Error).message}
        </div>
      ) : stats && regions ? (
        <>
          {/* Global Statistics Cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '24px',
              marginBottom: '32px',
            }}
          >
            <McCard >
              <div slot="body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--mds-color-text-secondary)', marginBottom: '4px' }}>
                    Total Regions
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 600 }}>{stats.totalRegions}</div>
                  <div style={{ fontSize: '11px', color: 'var(--mds-color-brand-primary)' }}>
                    EMEA, NAM, APAC, LATM
                  </div>
                </div>
                <McIcon icon="globe" style={{ fontSize: 48, color: 'var(--mds-color-brand-primary)', opacity: 0.3 }} />
              </div>
            </McCard>

            <McCard >
              <div slot="body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--mds-color-text-secondary)', marginBottom: '4px' }}>
                    Total Terminals
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 600 }}>{stats.totalTerminals}</div>
                  <div style={{ fontSize: '11px', color: 'var(--mds-color-feedback-success-text)' }}>
                    Globally Distributed
                  </div>
                </div>
                <McIcon icon="users" style={{ fontSize: 48, color: 'var(--mds-color-feedback-success-text)', opacity: 0.3 }} />
              </div>
            </McCard>

            <McCard >
              <div slot="body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--mds-color-text-secondary)', marginBottom: '4px' }}>
                    Default Region
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 600 }}>{stats.defaultRegion}</div>
                  <div style={{ fontSize: '11px', color: 'var(--mds-color-feedback-info-text)' }}>
                    Fallback Configuration
                  </div>
                </div>
                <McIcon icon="check-circle" style={{ fontSize: 48, color: 'var(--mds-color-feedback-info-text)', opacity: 0.3 }} />
              </div>
            </McCard>

            <McCard >
              <div slot="body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--mds-color-text-secondary)', marginBottom: '4px' }}>
                    DB Services
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 600 }}>{stats.totalRegions}</div>
                  <div style={{ fontSize: '11px', color: 'var(--mds-color-feedback-warning-text)' }}>
                    Regional Endpoints
                  </div>
                </div>
                <McIcon icon="database" style={{ fontSize: 48, color: 'var(--mds-color-feedback-warning-text)', opacity: 0.3 }} />
              </div>
            </McCard>
          </div>

          <h3 className="mds-typography--h3" style={{ marginBottom: '8px' }}>
            Regional Distribution
          </h3>
          <p className="mds-typography--body-2" style={{ color: 'var(--mds-color-text-secondary)', marginBottom: '24px' }}>
            Each region hosts a dedicated DB Service for optimal performance and data locality
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px',
              marginBottom: '32px',
            }}
          >
            {Object.entries(stats.terminalsByRegion).map(([regionName, terminalCount]) => (
              <McCard
                key={regionName}

                style={{
                  borderLeft: `4px solid ${getRegionColor(regionName)}`,
                }}
              >
                <div slot="body" style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h4 style={{ margin: 0 }}>{regionName}</h4>
                    <McBadge
                      label={`${terminalCount} terminals`}
                      display="inline"
                      style={{
                        '--mc-badge-background-color': getRegionColor(regionName),
                        '--mc-badge-text-color': 'white',
                      } as any}
                    />
                  </div>
                  <p className="mds-typography--body-2" style={{ color: 'var(--mds-color-text-secondary)', marginBottom: '8px' }}>
                    {regions[regionName]?.description}
                  </p>
                  <hr style={{ border: 0, borderTop: '1px solid var(--mds-color-border-subtle)', margin: '8px 0' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <McIcon icon="database" style={{ fontSize: 16, color: 'var(--mds-color-text-secondary)' }} />
                    <span style={{ fontSize: '11px', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {regions[regionName]?.dbServiceUrl}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--mds-color-text-secondary)' }}>
                      Percentage of Total:
                    </span>
                    <span style={{ fontSize: '11px', fontWeight: 600 }}>
                      {((terminalCount / stats.totalTerminals) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </McCard>
            ))}
          </div>

          {/* Terminal Lists (Accordions) */}
          <h3 className="mds-typography--h3" style={{ marginBottom: '8px' }}>
            Terminal Details by Region
          </h3>
          <p className="mds-typography--body-2" style={{ color: 'var(--mds-color-text-secondary)', marginBottom: '24px' }}>
            Expand each region to view configured terminals
          </p>

          {/* Search Bar */}
          <div style={{ marginBottom: '24px' }}>
            <McInput
              label="Search Terminals"
              value={searchTerm}
              onInput={(e: any) => setSearchTerm(e.detail.value)}
              placeholder="Search by terminal ID..."
              style={{ width: '100%' }}
            >
              <McIcon icon="magnifying-glass" slot="icon-start" style={{ color: 'var(--mds-color-text-secondary)' }} />
            </McInput>
          </div>

          <McAccordion style={{ marginBottom: '32px' }}>
            {Object.entries(regions).map(([regionName, regionConfig]: [string, RegionConfig]) => {
              const filteredTerminals = filterTerminals(regionConfig.terminals);
              const hasResults = filteredTerminals.length > 0;
              const isExpanded = expandedRegion === regionName;

              return (
                <McAccordionItem
                  key={regionName}
                  expanded={isExpanded}
                  accordionitemtoggle={(e: any) => {
                    const { expanded } = e.detail;
                    if (expanded) setExpandedRegion(regionName);
                    else if (expandedRegion === regionName) setExpandedRegion(null);
                  }}
                >
                  <div slot="heading" style={{
                    borderLeft: `4px solid ${getRegionColor(regionName)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    paddingLeft: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexGrow: 1 }}>
                      <McIcon icon="globe" style={{ color: getRegionColor(regionName) }} />
                      <div>
                        <div style={{ fontWeight: 600 }}>{regionName}</div>
                        <div style={{ fontSize: '11px', color: 'var(--mds-color-text-secondary)' }}>
                          {regionConfig.description}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <McBadge
                        label={`${filteredTerminals.length} / ${regionConfig.terminalCount} terminals`}
                        display="inline"
                        style={{
                          '--mc-badge-background-color': getRegionColor(regionName),
                          '--mc-badge-text-color': 'white',
                        } as any}
                      />
                    </div>
                  </div>

                  <div slot="body" style={{ padding: '24px' }}>
                    {hasResults ? (
                      <>
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px' }}>
                            DB Service Endpoint:
                          </div>
                          <div
                            style={{
                              padding: '8px',
                              backgroundColor: 'var(--mds-color-background-subtle)',
                              borderRadius: '4px',
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              wordBreak: 'break-all'
                            }}
                          >
                            {regionConfig.dbServiceUrl}
                          </div>
                        </div>

                        <hr style={{ border: 0, borderTop: '1px solid var(--mds-color-border-subtle)', margin: '16px 0' }} />

                        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '12px' }}>
                          Terminals:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {filteredTerminals.map((terminal) => (
                            <McBadge
                              key={terminal}
                              label={terminal}
                              appearance="info"
                              display="inline"
                              style={{
                                '--mc-badge-border-color': getRegionColor(regionName),
                                '--mc-badge-text-color': getRegionColor(regionName),
                              } as any}
                            />
                          ))}
                        </div>
                      </>
                    ) : (
                      <div style={{
                        padding: '12px',
                        backgroundColor: 'var(--mds-color-feedback-info-background)',
                        color: 'var(--mds-color-feedback-info-text)',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <McIcon icon="shield" />
                        <span>No terminals match your search criteria in this region.</span>
                      </div>
                    )}
                  </div>
                </McAccordionItem>
              );
            })}
          </McAccordion>

          {/* Architecture Info Card */}
          <McCard style={{ marginTop: '32px', backgroundColor: 'var(--mds-color-feedback-info-background)' }}>
            <div slot="body" style={{ padding: '24px', display: 'flex', gap: '16px' }}>
              <McIcon icon="shield" style={{ fontSize: 40, color: 'var(--mds-color-feedback-info-text)' }} />
              <div>
                <h4 style={{ margin: '0 0 8px 0' }}>How Multi-Region Works</h4>
                <p className="mds-typography--body-2" style={{ color: 'var(--mds-color-text-secondary)', marginBottom: '16px' }}>
                  When you execute a query or schedule, the Query Service automatically routes
                  requests to the appropriate regional DB Service based on the terminal location.
                </p>
                <p className="mds-typography--body-2" style={{ color: 'var(--mds-color-text-secondary)', margin: 0 }}>
                  <strong>Benefits:</strong> Reduced latency, data locality, regional isolation,
                  and 3-4x faster parallel execution across multiple regions.
                </p>
              </div>
            </div>
          </McCard>
        </>
      ) : (
        <div style={{ padding: '16px', backgroundColor: 'var(--mds-color-feedback-info-background)', borderRadius: '4px', color: 'var(--mds-color-feedback-info-text)' }}>
          No region data available.
        </div>
      )}
    </>
  );
}



